import { useMutation } from '@apollo/client';
import {
  AdminUpdateUserMutation,
  AdminUpdateUserMutationVariables,
  UpdateUserMutation,
  UpdateUserMutationVariables,
  User,
  UserUpdateInput,
} from 'lib/generated/graphql';
import { ADMIN_UPDATE_USER_MUTATION, UPDATE_USER_MUTATION } from 'lib/mutations';
import { GET_RECOMMENDED_HUDDLES, GET_USER_QUERY, GET_VIEWER } from 'lib/queries';
import { FunctionComponent, useRef, useState } from 'react';
import FormProfilePicture from 'src/screens/company/components/CompleteProfile/FormProfilePicture';
import { Button } from 'src/shared-components';
import useAuth from 'src/shared-components/Auth';
import FormTags from 'src/shared-components/forms/Tags';
import Title from 'src/shared-components/Modal/Title';
import FormDetails from './FormDetails';

const DEFAULT_USER_AVATAR = process.env.DEFAULT_USER_AVATAR || '';

interface UpdateUserProps {
  section: string;
  user: User;
  onUserUpdated: () => void;
  setIsLoading?: (isLoading: boolean) => void;
}

const sections = [
  {
    id: 'tags',
    title: 'Update tags',
    subtitle: 'These help match your profile with new projects.',
  },
  { id: 'details', title: 'Update your Profile', subtitle: null },
  {
    id: 'picture',
    title: 'Update your profile picture',
    subtitle: 'These helps make your profile recognizable to Huddle members.',
  },
];

const UpdateUser: FunctionComponent<UpdateUserProps> = ({
  section,
  user,
  onUserUpdated,
  setIsLoading,
}: UpdateUserProps) => {
  const [currentPhotoUrl, setCurrentPhotoUrl] = useState(user.photoUrl);
  const [currentSection] = useState<any>(sections.find(s => s.id === section));
  const formTagsRef = useRef<any>(null);
  // TODO: commented till implemented
  const [updateUserMutation] = useMutation<UpdateUserMutation, UpdateUserMutationVariables>(UPDATE_USER_MUTATION, {
    refetchQueries: [GET_VIEWER, GET_USER_QUERY, GET_RECOMMENDED_HUDDLES],
  });

  const [adminUpdateUserMutation] = useMutation<AdminUpdateUserMutation, AdminUpdateUserMutationVariables>(
    ADMIN_UPDATE_USER_MUTATION,
    {
      refetchQueries: [GET_VIEWER, GET_USER_QUERY, GET_RECOMMENDED_HUDDLES],
    },
  );

  const { user: authenticatedUser } = useAuth();

  const handleFormSubmitted = async (updateData: UserUpdateInput) => {
    if (setIsLoading) {
      setIsLoading(true);
    }
    try {
      if (authenticatedUser?.isCore) {
        await adminUpdateUserMutation({
          variables: {
            adminUpdateUserInput: updateData,
            adminUpdateUserUserId: user.id,
          },
        });
      } else {
        await updateUserMutation({
          variables: {
            updateData,
          },
        });
      }
      onUserUpdated();
      if (setIsLoading) {
        setIsLoading(false);
      }
    } catch (error) {
      console.error(error);
      if (setIsLoading) {
        setIsLoading(false);
      }
    }
  };

  const handleButtonClick = () => {
    if (section === 'picture') {
      handleFormSubmitted({ photoUrl: currentPhotoUrl });
    } else {
      formTagsRef.current.submitForm();
    }
  };

  const formatSocialLinksUpdate = (socialLinks: any) => {
    // TODO: add the part when a link is deleted
    const formToUpdate: any = {};
    const updateSocialLinks = socialLinks.filter((socialLink: any) => socialLink.id !== null);
    if (updateSocialLinks.length > 0) {
      formToUpdate.update = updateSocialLinks.map((socialLink: any) => ({
        where: { id: socialLink.id },
        data:
          socialLink.platformName === 'Twitter'
            ? { username: { set: socialLink.link }, link: { set: `https://twitter.com/${socialLink.link}` } }
            : { link: { set: socialLink.link } },
      }));
    }

    const newSocialLinks = socialLinks.filter((socialLink: any) => socialLink.id === null);
    if (newSocialLinks.length > 0) {
      formToUpdate.create = newSocialLinks.map((socialLink: any) => ({
        platformName: socialLink.platformName,
        link: socialLink.platformName === 'Twitter' ? `https://twitter.com/${socialLink.link}` : socialLink.link,
        username: socialLink.platformName === 'Twitter' ? socialLink.link : null,
      }));
    }
    return formToUpdate;
  };

  return (
    <div className="md:w-[30rem]">
      <Title title={currentSection.title} subtitle={currentSection.subtitle} />
      {section === 'details' && (
        <FormDetails
          user={user}
          ref={formTagsRef}
          onFormSubmitted={(name, bio, socialLinks) => {
            handleFormSubmitted({ name, bio, socialLinks: formatSocialLinksUpdate(socialLinks) });
          }}
        />
      )}
      {section === 'tags' && (
        <FormTags
          type="CREATOR"
          selectedIds={user.tags?.map(tag => tag.id)}
          ref={formTagsRef}
          onFormSubmitted={tagIds => {
            handleFormSubmitted({ tags: { set: tagIds.map((tagId: number) => ({ id: tagId })) } });
          }}
        />
      )}
      {section === 'picture' && (
        <FormProfilePicture
          photoRoundness="md"
          photoUrl={currentPhotoUrl || DEFAULT_USER_AVATAR}
          onFileUploaded={url => setCurrentPhotoUrl(url)}
          onRemovePhotoUrl={() => {
            setCurrentPhotoUrl(null);
          }}
        />
      )}

      <Button className="mt-2" size="lg" roundness="md" color="primary" fullWidth onClick={handleButtonClick}>
        {section === 'picture' ? 'Done' : 'Save'}
      </Button>
    </div>
  );
};

export default UpdateUser;
